import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import prettyBytes from "pretty-bytes";

import { ManifestType } from "@/apiClient";
import type { MakoAPIClient } from "@/apiClient";
import useApiClient from "@/hooks/useApiClient";
import { formatIsoDateStr } from "@/utils";

import WorkspacePanel from "./workspacePanel";

const UPLOAD_HISTORY_QUERY_KEY = ["home-upload-history"];
const PAGE_SIZE = 5;

const MANIFEST_TYPE_LABELS: Record<ManifestType, string> = {
  [ManifestType.OPEN_CRUISE_ARCHIVE]: "Open Cruise Archive",
  [ManifestType.SEALED_CRUISE_ARCHIVE]: "Sealed Cruise Archive",
  [ManifestType.CRUISE_ADDENDUM]: "Cruise Addendum",
};

const manifestTypeLabel = (type?: ManifestType): string =>
  type ? MANIFEST_TYPE_LABELS[type] : "Manifest";

const uploadHistoryQueryOptions = (api: MakoAPIClient) =>
  queryOptions({
    queryKey: UPLOAD_HISTORY_QUERY_KEY,
    queryFn: () => api.dataTransfer.listManifests({}),
    throwOnError: false,
    refetchOnWindowFocus: false,
  });

/**
 * Homepage "Upload History" panel: a searchable, paginated list of manifest
 * uploads. The /manifest endpoint returns the full, unsorted, unpaginated
 * array in one call, so sorting, search, and pagination are all done
 * client-side here (no extra requests). Degrades gracefully (error / empty
 * states) since manifest access may be restricted for some users.
 */
export default function UploadHistoryPanel({
  className,
}: {
  className?: string;
}) {
  const api = useApiClient();
  const { data, isPending, isError } = useQuery(uploadHistoryQueryOptions(api));

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const term = search.trim().toLowerCase();
  const sorted = [...(data ?? [])].sort((a, b) =>
    (b.created_dt ?? "").localeCompare(a.created_dt ?? "")
  );
  const filtered = term
    ? sorted.filter((manifest) =>
        [
          manifest.name,
          manifestTypeLabel(manifest.manifest_type),
          manifest.uploaded_by_email,
        ].some((field) => field?.toLowerCase().includes(term))
      )
    : sorted;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart =
    filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageItems = filtered.slice(
    pageStart === 0 ? 0 : pageStart - 1,
    pageEnd
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <WorkspacePanel
      className={className}
      title="Upload History"
      subtitle="Most recent manifest uploads"
    >
      {isPending ? (
        <ul className="space-y-2" aria-busy="true">
          {["s1", "s2", "s3", "s4", "s5"].map((id) => (
            <li
              key={id}
              className="h-14 animate-pulse rounded-lg bg-gray-200"
              aria-hidden="true"
            />
          ))}
        </ul>
      ) : isError ? (
        <p className="py-6 text-center text-sm text-grey-200">
          Could not load upload history right now.
        </p>
      ) : sorted.length === 0 ? (
        <p className="py-6 text-center text-sm text-grey-200">
          No manifest uploads yet.
        </p>
      ) : (
        <div className="space-y-3">
          <input
            type="search"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder="Search by name, type, or uploader..."
            aria-label="Search uploads"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-blue-300 transition-colors placeholder:text-grey-100 focus:border-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-100"
          />

          {pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-grey-200">
              No uploads match your search.
            </p>
          ) : (
            <ul className="space-y-2">
              {pageItems.map((manifest) => {
                const meta = [
                  manifestTypeLabel(manifest.manifest_type),
                  manifest.size != null ? prettyBytes(manifest.size) : null,
                  manifest.uploaded_by_email ?? null,
                ].filter(Boolean);

                // A deployment is selected by its public ID. The browser path
                // remains independent navigation state within that deployment.
                const deploymentPublicId =
                  manifest.real_asset_deployment?.public_id;

                const rowContent = (
                  <>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-blue-200 group-hover:underline">
                        {manifest.name}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-grey-200">
                        {meta.join(" · ")}
                      </p>
                    </div>
                    <p className="shrink-0 text-xs tabular-nums text-grey-200">
                      {manifest.created_dt
                        ? formatIsoDateStr(manifest.created_dt)
                        : "—"}
                    </p>
                  </>
                );

                return (
                  <li key={manifest.public_id ?? manifest.name}>
                    {deploymentPublicId ? (
                      <Link
                        to="/data/browse"
                        search={{
                          realAssetDeploymentPublicId: deploymentPublicId,
                          manifestType: manifest.manifest_type,
                        }}
                        className="group flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                      >
                        {rowContent}
                      </Link>
                    ) : (
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                        {rowContent}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
            <p className="text-xs tabular-nums text-grey-200">
              Showing {pageStart}–{pageEnd} of {filtered.length}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-grey-200 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm tabular-nums text-grey-200">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-grey-200 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </WorkspacePanel>
  );
}
